
import AppHeader from "@/components/AppHeader.vue";
import Page from "@/components/Page.vue";
import VDButton from "@/components/VDButton.vue";
import BatchRoleAssignmentDetail from "./BatchRoleAssignment/BatchRoleAssignmentDetail.vue";
import useBatchRoleAssignmentSidebar, {
  BatchRoleAssignmentResult,
} from "./BatchRoleAssignment/useBatchRoleAssignmentSidebar";
import useError from "@/composables/useError";
import {
  useGetBatchRoleAssignmentsQuery,
  useUploadBatchRoleAssignmentsCsvMutation,
  useLastBatchRoleAssignmentSimulationQuery,
  useLastBatchRoleAssignmentExecutionQuery,
  useDoBatchRoleAssignmentsMutation,
  useIsBatchRoleAssignmentRunningQuery,
  BatchRolesUploadResult,
} from "@/graphql/types";
import { useResult } from "@vue/apollo-composable";
import { defineComponent, ref } from "vue";
import { useToast } from "primevue/usetoast";
import gql from "graphql-tag";
import { displayDanishTimestamp } from "@/composables/utils";

gql`
  query GetBatchRoleAssignments {
    batchRoleAssignments {
      email
      roleName
      roleId
      orgCVR
      organizationId
      organizationName
    }
  }
`;
gql`
  query IsBatchRoleAssignmentRunning {
    isBatchRoleAssignmentRunning
  }
`;

gql`
  query LastBatchRoleAssignmentSimulation {
    lastBatchRoleAssignmentSimulation {
      settingId
      key
      value
    }
  }
`;

gql`
  query LastBatchRoleAssignmentExecution {
    lastBatchRoleAssignmentExecution {
      settingId
      key
      value
    }
  }
`;

gql`
  mutation UploadBatchRoleAssignmentsCsv($input: UploadCsvInputType!) {
    uploadBatchRoleAssignmentsCsv(input: $input) {
      success
      errors
      validRoleAssignmentItems {
        email
        roleName
        roleId
        orgCVR
        organizationId
      }
    }
  }
`;

gql`
  mutation DoBatchRoleAssignments($input: BatchRoleAssignmentOption!) {
    doBatchRoleAssignments(input: $input) {
      isSimulation
      success
      errors
      totalAssignments
      totalUsersAssignment
      processedAt
      finishedAt
      assignedRoles {
        email
        roleName
        roleId
        orgCVR
        organizationId
      }
    }
  }
`;

export default defineComponent({
  name: "BatchRoleAssignment",
  components: {
    AppHeader,
    Page,
    VDButton,
    BatchRoleAssignmentDetail,
  },
  setup() {
    const toast = useToast();

    // Sidebar state management
    const sidebar = useBatchRoleAssignmentSidebar();

    // Query for existing batch role assignments
    const { result, loading, error, refetch } =
      useGetBatchRoleAssignmentsQuery();
    const batchRoleAssignments = useResult(result);

    // Historical queries
    const { result: simulationResult, refetch: refetchSimulation } =
      useLastBatchRoleAssignmentSimulationQuery();
    const lastSimulation = useResult(simulationResult);

    const { result: executionResult, refetch: refetchExecution } =
      useLastBatchRoleAssignmentExecutionQuery();
    const lastExecution = useResult(executionResult);

    // File upload state
    const selectedFile = ref<File | null>(null);
    const fileInput = ref<HTMLInputElement | null>(null);
    const uploadResult = ref<BatchRolesUploadResult | null>(null);
    const uploadError = ref<string | null>(null);

    // Simple type for processing result
    interface ProcessingResult {
      success?: boolean | null | undefined;
      totalAssignments?: number | null | undefined;
      totalUsersAssignment?: number | null | undefined;
      isSimulation?: boolean | null | undefined;
      processedAt?: string | null | undefined;
      errors?: string[] | null | undefined;
    }

    // Historical result interfaces based on C# classes
    interface BatchRoleAssignmentItem {
      email: string;
      roleName: string;
      roleId: string;
      orgCVR: string;
      organizationId: string;
    }

    // Processing state
    const processingResult = ref<any>(null);
    const isSimulation = ref<boolean>(false);

    // Query for processing status
    const { result: runningResult, loading: runningLoading } =
      useIsBatchRoleAssignmentRunningQuery();
    const isProcessingRunning = useResult(runningResult);

    // Upload mutation
    const { mutate: uploadCsv, loading: uploadLoading } =
      useUploadBatchRoleAssignmentsCsvMutation({});

    // Processing mutation
    const {
      mutate: doBatchRoleAssignmentsMutation,
      loading: processingLoading,
    } = useDoBatchRoleAssignmentsMutation({});

    // File selection handler
    function onFileSelected(event: Event) {
      const target = event.target as HTMLInputElement;
      const file = target.files?.[0];

      if (file) {
        // Validate file type
        if (!file.name.toLowerCase().endsWith(".csv")) {
          toast.add({
            severity: "error",
            summary: "Ugyldig fil type",
            detail: "Kun CSV filer er tilladt",
            life: 5000,
          });
          return;
        }

        // Validate file size (max 5MB)
        if (file.size > 5 * 1024 * 1024) {
          toast.add({
            severity: "error",
            summary: "Fil for stor",
            detail: "Filen må maksimalt være 5MB",
            life: 5000,
          });
          return;
        }

        selectedFile.value = file;
        uploadResult.value = null; // Clear previous results
        uploadError.value = null; // Clear previous errors
      }
    }

    // File upload handler
    async function uploadCsvFile() {
      if (!selectedFile.value) return;

      // Clear previous errors
      uploadError.value = null;

      try {
        const fileContent = await readFileAsText(selectedFile.value);

        const result = await uploadCsv({
          input: {
            csvContent: fileContent,
          },
        });

        if (result?.data?.uploadBatchRoleAssignmentsCsv) {
          uploadResult.value = result.data.uploadBatchRoleAssignmentsCsv;

          if (result.data.uploadBatchRoleAssignmentsCsv.success) {
            toast.add({
              severity: "success",
              summary: "Upload succesfuld",
              detail: `${
                result.data.uploadBatchRoleAssignmentsCsv
                  .validRoleAssignmentItems?.length || 0
              } rolletildeling(er) blev uploadet`,
              life: 5000,
            });

            // Refetch the batch role assignments to show updated data
            refetch();
          } else if (result.data.uploadBatchRoleAssignmentsCsv.errors?.length) {
            const errorMessage =
              result.data.uploadBatchRoleAssignmentsCsv.errors.join(", ");
            uploadError.value = errorMessage;
          }
        } else if (result?.errors?.length) {
          // Handle GraphQL errors
          const errorMessage = result.errors
            .map((error) => error.message)
            .join(", ");
          uploadError.value = errorMessage;
        }
      } catch (err: any) {
        let errorMessage = "Der opstod en fejl under upload af filen";

        // Extract meaningful error message from Apollo errors
        if (err?.graphQLErrors?.length > 0) {
          // GraphQL errors from Apollo
          errorMessage = err.graphQLErrors
            .map((error: any) => error.message)
            .join(", ");
        } else if (err?.networkError?.message) {
          // Network errors from Apollo
          errorMessage = err.networkError.message;
        } else if (err?.message) {
          // General error message
          errorMessage = err.message;
        }

        uploadError.value = errorMessage;
        toast.add({
          severity: "error",
          summary: "Upload fejl",
          detail: errorMessage,
          life: 5000,
        });
      }
    }

    // Helper function to read file as text
    function readFileAsText(file: File): Promise<string> {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = () => reject(reader.error);
        reader.readAsText(file);
      });
    }

    // Helper function to format file size
    function formatFileSize(bytes: number): string {
      if (bytes === 0) return "0 Bytes";

      const k = 1024;
      const sizes = ["Bytes", "KB", "MB", "GB"];
      const i = Math.floor(Math.log(bytes) / Math.log(k));

      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
    }

    // Batch role assignment function
    async function doBatchRoleAssignments(simulation: boolean) {
      isSimulation.value = simulation;
      processingResult.value = null;

      try {
        const result = await doBatchRoleAssignmentsMutation({
          input: {
            isSimulation: simulation,
          },
        });

        if (result?.data?.doBatchRoleAssignments) {
          processingResult.value = result.data.doBatchRoleAssignments;

          if (result.data.doBatchRoleAssignments.success) {
            toast.add({
              severity: "success",
              summary: simulation
                ? "Simulation succesfuld"
                : "Udførelse succesfuld",
              detail: `${
                result.data.doBatchRoleAssignments.totalAssignments || 0
              } rolle tildelinger blev ${simulation ? "simuleret" : "udført"}`,
              life: 5000,
            });

            // Refetch historical data after successful execution
            if (simulation) {
              // Refetch simulation history
              refetchSimulation();
            } else {
              // Refetch execution history
              refetchExecution();
            }

            // Refetch data after successful execution
            if (!simulation) {
              refetch();
            }
          } else {
            toast.add({
              severity: "error",
              summary: simulation ? "Simulation fejl" : "Udførelse fejl",
              detail: "Der opstod en fejl under behandlingen",
              life: 8000,
            });
          }
        }
      } catch (err: any) {
        let errorMessage = `Der opstod en fejl under ${
          simulation ? "simulation" : "udførelse"
        }`;
        if (err?.graphQLErrors?.length > 0) {
          errorMessage = err.graphQLErrors
            .map((error: any) => error.message)
            .join(", ");
        } else if (err?.networkError?.message) {
          errorMessage = err.networkError.message;
        } else if (err?.message) {
          errorMessage = err.message;
        }

        toast.add({
          severity: "error",
          summary: simulation ? "Simulation fejl" : "Udførelse fejl",
          detail: errorMessage,
          life: 5000,
        });
      }
    }

    // Helper function to parse historical result JSON
    function parseHistoricalResult(
      historyItem: any
    ): BatchRoleAssignmentResult | null {
      if (!historyItem?.value) {
        return null;
      }

      try {
        // historyItem.value contains the JSON string with escape sequences
        // Parse it directly to get the BatchRoleAssignmentResult object
        const rawParsed = JSON.parse(historyItem.value);

        // Map Pascal case properties to camel case to match the interface
        const parsed: BatchRoleAssignmentResult = {
          IsSimulation: rawParsed.IsSimulation,
          Success: rawParsed.Success,
          Errors: rawParsed.Errors || [],
          ProcessedAt: rawParsed.ProcessedAt,
          FinishedAt: rawParsed.FinishedAt,
          TotalUsersAssignment: rawParsed.TotalUsersAssignment,
          TotalAssignments: rawParsed.TotalAssignments,
          AssignedRoles: (rawParsed.AssignedRoles || []).map((role: any) => ({
            email: role.Email,
            roleName: role.RoleName,
            roleId: role.RoleId,
            orgCVR: role.OrgCVR,
            organizationName: role.OrganizationName,
            organizationId: role.OrganizationId,
            failureReason: role.FailureReason,
            isSuccess: role.IsSuccess,
            userName: role.UserName,
          })),
        };

        return parsed;
      } catch (error) {
        return null;
      }
    }

    // Function to show detail sidebar
    function showDetailFor(result: BatchRoleAssignmentResult) {
      sidebar.showSidebar(result);
    }

    // Function for simulation button
    function showSimulationDetails() {
      // Get the actual data from the GraphQL query
      const data = simulationResult.value?.lastBatchRoleAssignmentSimulation;

      if (data) {
        const result = parseHistoricalResult(data);
        if (result) {
          showDetailFor(result);
        }
      }
    }

    // Function for execution button
    function showExecutionDetails() {
      // Get the actual data from the GraphQL query
      const data = executionResult.value?.lastBatchRoleAssignmentExecution;

      if (data) {
        const result = parseHistoricalResult(data);
        if (result) {
          showDetailFor(result);
        }
      }
    }

    return {
      // Data
      batchRoleAssignments,
      loading,
      error: useError(error),

      // Historical data
      lastSimulation,
      lastExecution,

      // File upload
      selectedFile,
      fileInput,
      uploadResult,
      uploadLoading,
      uploadError,

      // Processing
      processingResult,
      processingLoading,
      isProcessingRunning,
      isSimulation,

      // Sidebar
      sidebar,

      // Methods
      onFileSelected,
      uploadCsvFile,
      formatFileSize,
      doBatchRoleAssignments,
      parseHistoricalResult,
      showDetailFor,
      showSimulationDetails,
      showExecutionDetails,
      displayDanishTimestamp,
    };
  },
});
